/*
 *  Copyright (C) 2025 - SimpliCity Digital Inc - All Rights Reserved
 */

import React, { useEffect, useRef, useState } from "react";
import "swiper/css";
import "swiper/css/pagination";
import { Swiper, SwiperSlide } from "swiper/react";
import { Autoplay, Pagination } from "swiper/modules";
import { isArrayEmpty } from "cms/utils/empty-exists";
import { useImageLoader } from "cms/components/hero-carousel/use-image-map-loader";
import { HeroSlideshowSlide } from "./hero-slideshow-slide";
import { EmptyHeroSlideshowSlide } from "./empty-hero-slideshow-slide";
import { ReactComponent as PauseIcon } from "cms/assets/icons/pause.svg";
import { ReactComponent as PlayIcon } from "cms/assets/icons/play.svg";

const HeroSlideshow = (props) => {

    const { component } = props;
    const {
        getImageData,
        preloadImageArray,
        preloadImage,
    } = useImageLoader();
    const { slides = [] } = component || {};

    const [index, setIndex] = useState(0);
    const [images, setImages] = useState(null);
    const [isAutoPlayOn, setIsAutoPlayOn] = useState(true);
    const swiperRef = useRef(null);

    const hasContent = !isArrayEmpty(slides);
    // const total = slides?.length ?? 0

    const buildSlidesData = async (_slides) => {
        if (!_slides) {
            return;
        }
        const datas = [];
        const first_data = await getImageData(_slides[0]?.image?.image);
        const first_image = await preloadImage(first_data);
        setImages([first_image]);
        try {
            for (const slide of _slides) {
                const image = slide?.image;
                const image_guid = image?.image;
                if (image_guid) {
                    const data = await getImageData(image_guid);
                    datas.push(data);
                }
            }
            const _images = await preloadImageArray(datas);
            setImages(_images);
        } catch (err) {
            console.error(err);
        }
        return images;
    };

    useEffect(() => {
        buildSlidesData(slides);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [slides]);

    function moveIndex (increment) {
        let idx = index + increment;
        if (idx > slides.length - 1) {
            idx = 0;
        }
        if (idx < 0) {
            idx = slides.length - 1;
        }
        setIndex(idx);
    }

    const toggleAutoPlay = () => {
        if (!swiperRef.current) {
            return;
        }

        if (isAutoPlayOn) {
            swiperRef.current.autoplay.stop();
            setIsAutoPlayOn(false);
        } else {
            swiperRef.current.autoplay.start();
            setIsAutoPlayOn(true);
        }
    };

    if (!hasContent) {
        return <EmptyHeroSlideshowSlide />;
    } else {
        return (
            <div className='hero-swiper-container'>
                <Swiper
                    modules={[Autoplay, Pagination]}
                    onSwiper={(swiper) => (swiperRef.current = swiper)}
                    autoplay={{ delay: 5000, disableOnInteraction: false }}
                    pagination={{ clickable: true, el: "#custom-pagination" }}
                    loop={true}
                >
                    {slides.map((_, idx) => {
                        return (
                            <SwiperSlide key={idx}>
                                <HeroSlideshowSlide
                                    slides={slides}
                                    images={images}
                                    key={idx}
                                    index={idx}
                                    moveIndex={moveIndex}
                                />
                            </SwiperSlide>
                        );
                    })}
                </Swiper>
                <div className="custom-pagination-container">
                    <div className="custom-pagination-inner-container">
                        <div id="custom-pagination" />
                        <button className='play-pause-btn' onClick={toggleAutoPlay}>
                            {isAutoPlayOn ? <PauseIcon /> : <PlayIcon />}
                        </button>
                    </div>
                </div>
            </div>
        );
    }
};

HeroSlideshow.displayName = "HeroSlideshow";
export { HeroSlideshow };
